import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  CURRENCIES,
  Currency,
  ProductKey,
  formatMoney,
  formatPrice,
  getPrice,
  isCurrency,
} from "@/lib/pricing";

const STORAGE_KEY = "doh_currency";

// Region → currency. Everything else falls back to EUR.
const REGION_CURRENCY: Record<string, Currency> = {
  US: "USD", PR: "USD", GU: "USD", VI: "USD",
  GB: "GBP", UK: "GBP", IM: "GBP", JE: "GBP", GG: "GBP",
  CA: "CAD",
  AU: "AUD", NZ: "AUD",
};

const TIMEZONE_CURRENCY: Array<[RegExp, Currency]> = [
  [/^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Detroit|Anchorage|Honolulu|Indiana|Kentucky|Boise|Juneau|Sitka|Nome|Adak|Menominee|North_Dakota)/, "USD"],
  [/^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|Regina|St_Johns|Montreal|Moncton|Whitehorse|Yellowknife|Iqaluit)/, "CAD"],
  [/^Europe\/London$/, "GBP"],
  [/^Australia\//, "AUD"],
  [/^Pacific\/Auckland$/, "AUD"],
];

function detectCurrency(): Currency {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    for (const [re, cur] of TIMEZONE_CURRENCY) if (re.test(tz)) return cur;

    const locales = [
      ...(navigator.languages || []),
      navigator.language || "",
    ].filter(Boolean);
    for (const loc of locales) {
      const region = loc.split("-")[1]?.toUpperCase();
      if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region];
    }
  } catch {
    /* ignore */
  }
  return "EUR";
}

interface CurrencyContextValue {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  currencies: readonly Currency[];
  /** Price of a catalogue product in the active currency. */
  price: (product: ProductKey) => number;
  /** Formatted price of a catalogue product, e.g. "$21.99". */
  priceLabel: (product: ProductKey) => string;
  /** Format an arbitrary amount in the active currency. */
  format: (amount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isCurrency(stored)) return stored;
    } catch {
      /* ignore */
    }
    return "EUR";
  });

  // Auto-detect on first ever visit (no stored choice).
  useEffect(() => {
    try {
      if (!isCurrency(localStorage.getItem(STORAGE_KEY))) {
        setCurrencyState(detectCurrency());
      }
    } catch {
      /* ignore */
    }
  }, []);

  const setCurrency = useCallback((c: Currency) => {
    setCurrencyState(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      setCurrency,
      currencies: CURRENCIES,
      price: (product) => getPrice(product, currency),
      priceLabel: (product) => formatPrice(product, currency),
      format: (amount) => formatMoney(amount, currency),
    }),
    [currency, setCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within a CurrencyProvider");
  return ctx;
}
